import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row, View } from "@amzn/stencil-react-components/layout";
import { ProgressBar, ProgressBarStatus } from "@amzn/stencil-react-components/progress-bar";
import { Text } from "@amzn/stencil-react-components/text";
import { RatingCounts } from "lib/services/catalogical-models";
import React from "react";
import { FunctionComponent } from "react";

import { SubmitReviewForm } from "../SubmitReviewForm";
import { StarAverageRating, StarAverageRatingSize } from "./StarAverageRating";

export enum StarDetailedRatingSize {
  Small,
  Medium,
}
export interface StarDetailedRatingProps {
  size: StarDetailedRatingSize;
  ratingCounts?: RatingCounts | undefined;
  averageRating?: number;
  voteNumber?: number;
  showCheckReviewsButton?: boolean;
  checkReviewsButtonFunction?: () => void;
  button?: JSX.Element;
}

const getSizes = (size: StarDetailedRatingSize) => {
  switch (size) {
    case StarDetailedRatingSize.Small:
      return { starRating: StarAverageRatingSize.Small, fontSize: "T100" };
    case StarDetailedRatingSize.Medium:
      return { starRating: StarAverageRatingSize.Small, fontSize: "T200" };
    default:
      return { starRating: StarAverageRatingSize.ExtraSmall, fontSize: "T100" };
  }
};

const calculatePercentage = (value: number, total: number) => {
  if (total === 0) {
    return 0;
  }
  return Math.round((value / total) * 100);
};

export const StarDetailedRating: FunctionComponent<StarDetailedRatingProps> = ({
  size,
  averageRating,
  voteNumber,
  ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  button,
}) => {
  const { starRating, fontSize } = getSizes(size);

  const renderProgressBars = () => {
    const progressBars = [];
    for (let starNumber = 5; starNumber > 0; starNumber--) {
      const ratingCount = ratingCounts[starNumber as keyof RatingCounts];
      const percentage = voteNumber ? calculatePercentage(ratingCount, voteNumber) : 0;
      progressBars.push(
        <Row
          alignItems="center"
          columnGap={"12px"}
          key={`${starNumber}-star-bar-row`}
          dataTestId={`${starNumber}-star-bar-row`}
        >
          <Text fontSize={fontSize} style={{ whiteSpace: "nowrap" }}>
            {starNumber} star
          </Text>
          <View width={"80%"}>
            <ProgressBar
              progress={percentage / 100}
              status={ProgressBarStatus.Positive}
              dataTestId={`${starNumber}-star-bar`}
            />
          </View>
          <Text fontSize={fontSize} style={{ whiteSpace: "nowrap", minWidth: "30px" }}>
            {percentage}%
          </Text>
        </Row>
      );
    }
    return progressBars;
  };

  return (
    <View width={"auto"} dataTestId="star-detailed-rating">
      <Row alignItems="center">
        <StarAverageRating size={starRating} averageRating={averageRating} voteNumber={voteNumber} />
      </Row>
      <Col rowGap={"8px"} margin={["8px", "0", "8px", "0"]}>
        {renderProgressBars()}
      </Col>
      {button && (
        <Row alignItems="center" justifyContent="center">
          {button}
        </Row>
      )}
    </View>
  );
};
